// The 4 chatbot characters (Inès / Léo / Enzo / Maya), each with a header avatar
// and a full-body "success" visual. A character is picked once per browser session
// and persisted in sessionStorage (see resolveCharacterKey) so reopening the chat —
// or refreshing the page — keeps the same face/name until the tab is closed; the
// success screen therefore also matches. See INSTALL.md decision 2026-05-22. Inès is
// the V1 default.

export interface Character {
  key: string
  name: string
  avatar: string
  success: string
}

const ines: Character = {
  key: 'ines',
  name: 'Inès',
  avatar: new URL('../assets/ines-avatar.png', import.meta.url).href,
  success: new URL('../assets/ines-success.webp', import.meta.url).href,
}
const leo: Character = {
  key: 'leo',
  name: 'Léo',
  avatar: new URL('../assets/leo-avatar.png', import.meta.url).href,
  success: new URL('../assets/leo-success.webp', import.meta.url).href,
}
const enzo: Character = {
  key: 'enzo',
  name: 'Enzo',
  avatar: new URL('../assets/enzo-avatar.png', import.meta.url).href,
  success: new URL('../assets/enzo-success.webp', import.meta.url).href,
}
const maya: Character = {
  key: 'maya',
  name: 'Maya',
  avatar: new URL('../assets/maya-avatar.png', import.meta.url).href,
  success: new URL('../assets/maya-success.webp', import.meta.url).href,
}

export const CHARACTERS: Character[] = [ines, leo, enzo, maya]
export const DEFAULT_CHARACTER_KEY = 'ines'

// Resolve a character by its persisted key, falling back to the default (Inès).
export function characterByKey(key: string): Character {
  return CHARACTERS.find((c) => c.key === key) || ines
}

// Pick a random character key. `rng` is injectable for deterministic tests.
export function pickRandomCharacterKey(rng: () => number = Math.random): string {
  return CHARACTERS[Math.floor(rng() * CHARACTERS.length)]!.key
}

// sessionStorage key under which the chosen character is remembered for the tab session.
export const CHARACTER_STORAGE_KEY = 'nt_signup_character'

// sessionStorage, or null when it's unavailable. Merely *accessing* the property throws
// in some privacy modes / sandboxed iframes, so the read is guarded — a null result just
// means "no persistence", never a crash.
function safeSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

// Restore the character chosen earlier in this browser session, or pick + persist a new
// one. Keeps the same face/name across chat reopens and page refreshes until the tab
// closes. `rng`/`storage` are injectable for deterministic, storage-agnostic tests.
export function resolveCharacterKey(
  rng: () => number = Math.random,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage(),
): string {
  const stored = storage?.getItem(CHARACTER_STORAGE_KEY)
  if (stored && CHARACTERS.some((c) => c.key === stored)) return stored

  const key = pickRandomCharacterKey(rng)
  storage?.setItem(CHARACTER_STORAGE_KEY, key)
  return key
}
